import { skipToken } from '@reduxjs/toolkit/query';
import { SnippetListData, useGetCollabSnippetDetailsQuery, useGetSnippetCollabListQuery, useGetSnippetDetailsQuery, useGetSnippetListQuery } from 'api/snippet';
import { Bookmark, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Dialog, Input, Label } from 'ui/components';
import { DataStateOverlay } from 'ui/components/DataLoadOverlay';
import { cn } from 'utils/cn';
import { getUser } from '../../../../store/userSlice';
import NoCannedResponse from './no-canned-reponse';

interface Props {
    handleSubmit?: (data: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    showIcon?: boolean;
    taskId?: string;
    siteId: string;
    workspaceId: number;
}

function CannedResponsesModal({ handleSubmit, open, setOpen, showIcon = true, taskId, siteId, workspaceId }: Props) {
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [showNoFilter, setShowNoFilter] = useState(false);
    const [search, setSearch] = useState('');
    const [filteredCannedResponses, setFilteredCannedResponses] = useState<SnippetListData[]>([]);
    const useCollab = location.pathname.includes('/collaborate');
    const user = useSelector(getUser);
    const dispatch = useDispatch();

    // Fixed hook usage for canned responses
    const collabCanned = useGetSnippetCollabListQuery(
        useCollab ? { websiteId: siteId, taskId, workspace_id: workspaceId } : skipToken
    );
    const nonCollabCanned = useGetSnippetListQuery(
        !useCollab ? { workspace_id: workspaceId } : skipToken
    );
    const { data: cannedResponses, isFetching: isLoadingCannedResponses } = useCollab ? collabCanned : nonCollabCanned;

    const collabSnippetParams = (useCollab && filteredCannedResponses?.[selectedIndex]?.id && open)
        ? {
            id: filteredCannedResponses[selectedIndex].id,
            taskId,
            websiteId: siteId,
            workspace_id: workspaceId
        }
        : skipToken;
    const collabSnippet = useGetCollabSnippetDetailsQuery(collabSnippetParams);
    const nonCollabSnippetParams = (!useCollab && filteredCannedResponses?.[selectedIndex]?.id && open)
        ? {
            id: filteredCannedResponses[selectedIndex].id,
            taskId,
            workspace_id: workspaceId
        }
        : skipToken;
    const nonCollabSnippet = useGetSnippetDetailsQuery(nonCollabSnippetParams);

    const { data: selectedSnippet, isFetching: isLoadingSelectedSnippet } = useCollab ? collabSnippet : nonCollabSnippet;

    const [isExpanded, setIsExpanded] = useState(false);



    useEffect(() => {
        if (!cannedResponses) return;
        const filtered = cannedResponses.filter((response) =>
            response.title.toLowerCase().includes(search.toLowerCase())
        );
        setFilteredCannedResponses(filtered);
        setShowNoFilter(filtered.length === 0);
    }, [search, cannedResponses]);

    useEffect(() => {
        if (!cannedResponses || cannedResponses.length === 0) return;
        setFilteredCannedResponses(cannedResponses);
    }, [cannedResponses]);

    const showNoSnippet = !isLoadingCannedResponses && filteredCannedResponses.length === 0;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {showIcon && (
                <Dialog.Trigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 hover:bg-transparent"
                        tooltip={t('canned_responses.tooltip.insert', 'Insert a canned response')}
                        toolTipPortal
                    >
                        <Bookmark size={18} aria-hidden="true" className='text-gray-400 hover:text-gray-600 dark:hover:text-textcolor' />
                    </Button>
                </Dialog.Trigger>
            )}
            <Dialog.Content
                container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                className="flex mh700:max-h-[90vh] min-h-[25vh] max-h-[50vh] flex-col overflow-hidden sm:max-w-[800px] h-full p-0 z-50"
            >
                {user.raw?.plan?.canned_response_enabled === false ? (
                    <>
                        <Dialog.Header>
                            <Dialog.Title>{t('canned_responses.upgrade.title', 'Let\'s Unlock This Feature')}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Description className="flex gap-2">
                            <img
                                src="https://wpfeedback-image.s3.us-east-2.amazonaws.com/media/Canned+Responses.png"
                                alt={t('canned_responses.upgrade.image_alt', 'Canned Responses feature preview')}
                                className="aspect-video max-w-[500px]"
                            />
                            <div className="flex flex-col items-center justify-between gap-8 rounded-md border p-4">
                                <p className="text-xl font-bold">{t('canned_responses.upgrade.feature_name', 'Canned Responses')}</p>
                                <span className="text-sm">
                                    {t('canned_responses.upgrade.description', 'Getting tired of typing out the same messages over and over again? Speed up your workflow even more by unlocking the ability to create canned responses that can be used inside your email inbox and tasks. Also import templates written by professionals, see how they speak to their clients and use copy that get\'s results!')}
                                </span>
                                <Button
                                    className={cn('w-fit text-left text-gray-600')}
                                    size="sm"
                                    onClick={() => {
                                        setOpen(false);
                                        dispatch({ type: 'HANDLE_PLANS_DIALOG', params: true });
                                    }}
                                    leftIcon={<Plus size={18} aria-hidden="true" />}
                                >
                                    {t('canned_responses.upgrade.button', 'Upgrade Plans')}
                                </Button>
                            </div>
                        </Dialog.Description>
                    </>
                ) : (
                    <div className="relative flex flex-1 flex-col overflow-hidden">
                        <DataStateOverlay
                            text={t('canned_responses.loading.templates', 'Loading Templates...')}
                            visible={isLoadingCannedResponses}
                        />
                        <div className="grid h-full grid-cols-[300px_1fr] overflow-hidden">
                            <div className="flex h-full flex-col overflow-hidden border-r border-border">
                                <div className="p-4 pb-0">
                                    <div className="relative mb-4">
                                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 transform text-textcolor" aria-hidden="true" />
                                        <Input
                                            placeholder={t('canned_responses.search.placeholder', 'Search templates...')}
                                            className="w-full pl-10"
                                            onChange={(e) => setSearch(e.target?.value)}
                                            value={search}
                                        />
                                    </div>
                                    <Label className="text-textcolor mt-1 text-xs font-semibold">
                                        {t('canned_responses.labels.templates', 'Canned Response Templates')}
                                    </Label>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4 pt-0">
                                    {showNoSnippet ? (
                                        <NoCannedResponse showNoFilter={showNoFilter} />
                                    ) : (
                                        <div className="grid gap-2 py-2">
                                            {filteredCannedResponses?.map((response, index) => (
                                                <Button
                                                    key={index}
                                                    variant="outline"
                                                    size="sm"
                                                    leftIcon={<Bookmark size={16} aria-hidden="true" />}
                                                    className={cn(
                                                        'hover:bg-tertiary hover:text-white active:bg-tertiary/80 grid grid-flow-col items-center justify-start gap-2 text-gray-600',
                                                        selectedIndex === index && 'bg-tertiary/5 text-tertiary outline-tertiary outline outline-2 transition-all duration-100'
                                                    )}
                                                    onClick={() => setSelectedIndex(index)}
                                                >
                                                    <span className="max-w-[250px] truncate text-sm capitalize">
                                                        {response.title}
                                                    </span>
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="relative flex h-full flex-col overflow-hidden p-2">
                                <div className="p-4 pt-6">
                                    <DataStateOverlay
                                        visible={isLoadingSelectedSnippet}
                                        text={t('canned_responses.loading.template', 'Fetching Template')}
                                    />
                                    <h2
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className={cn('text-xl font-bold text-textcolor', isExpanded ? "line-clamp-none" : "line-clamp-2")}>
                                        {selectedSnippet?.title}
                                    </h2>
                                    {selectedSnippet?.notes && (
                                        <p className="text-muted-foreground mb-4">{selectedSnippet?.notes}</p>
                                    )}
                                </div>
                                <div className="flex-1 overflow-y-auto px-4 pb-8 pt-0 text-sm text-textcolor">
                                    <div dangerouslySetInnerHTML={{ __html: selectedSnippet?.content ?? '' }}></div>
                                </div>
                                <div className="flex justify-end border-t p-4 pb-2 pr-2 border-border">
                                    <Button
                                        className={cn('w-fit text-left text-gray-600')}
                                        size="sm"
                                        onClick={() => {
                                            if (handleSubmit) {
                                                handleSubmit(selectedSnippet?.content ?? '');
                                                setOpen(false);
                                            }
                                        }}
                                        leftIcon={<Plus size={18} aria-hidden="true" />}
                                    >
                                        {t('canned_responses.buttons.use_template', 'Use Template')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog.Content>
        </Dialog>
    );
}

export default CannedResponsesModal;